import { DocumentCategory } from '@/app/api/types/redpim.types'
import { createElement, useEffect, useRef, useState } from 'react'

export function useSearch(query: string, lang: string) {
	const [loading, setLoading] = useState(false)
	const [products, setProducts] = useState([])
	const [accessories, setAccessories] = useState([])
	const [resources, setResources] = useState<
		{
			itemPath: string
			itemType: string
			documentCategory: DocumentCategory
			metadataStorageName: string
			metadataStorageSize: string
		}[]
	>([])
	const [dt, setDt] = useState<{
		serialNumber: string
		partNumber: string
		family: string
		name: string
	} | null>(null)

	// Track the last processed query to prevent duplicate requests
	const lastProcessedQueryRef = useRef<string>('')
	const lastProcessedLangRef = useRef<string>('')
	const requestTimeoutRef = useRef<NodeJS.Timeout | null>(null)

	useEffect(() => {
		// Clear any pending timeout
		if (requestTimeoutRef.current) {
			clearTimeout(requestTimeoutRef.current)
		}

		// Reset state for new queries
		setProducts([])
		setResources([])
		setDt(null)

		// Don't make requests for queries less than 3 characters
		if (query.length < 3) {
			setLoading(false)
			return
		}

		// Check if this is the same query and language as last processed
		if (
			query === lastProcessedQueryRef.current &&
			lang === lastProcessedLangRef.current
		) {
			return
		}

		// Add a small delay to prevent rapid successive requests
		requestTimeoutRef.current = setTimeout(() => {
			const abortController = new AbortController()

			setLoading(true)

			fetch(
				`/api/search?query=${encodeURIComponent(query)}&locale=${encodeURIComponent(lang)}`,
				{
					signal: abortController.signal,
				},
			)
				.then(async (response) => {
					if (!response.ok) {
						throw new Error(`Search request failed: ${response.status}`)
					}

					const data = await response.json()

					setResources(data.documents || [])
					setDt(data.dt || null)

					setProducts([
						...(data.products || []).map((product: any) => ({
							id: product.pimId,
							title: product.name,
							bodyExcerpt:
								product.uspIntl?.[lang] || product.shortDescriptionIntl?.[lang],
							imageSrc: product.image?.url || '/images/driver.svg',
							url: `/p/${product.slug}`,
						})),
						...(data.items || []).map((item: any) => ({
							id: item.partnumber,
							title: item.model,
							// ⚠️In new PIM we do not retrieve category anymore but products (since category can be multiple). But get products from db for many items can be expensive.
							bodyExcerpt:
								item.shortDescriptionsIntl?.[lang] ||
								item.descriptionsIntl?.[lang] ||
								item.model,
							imageSrc: item.thumbnail
								? item.thumbnail +
									'/jcr:content/renditions/cq5dam.web.400.400.jpeg'
								: '/images/box.svg',
							url: `/products/${item.partnumber}`,
						})),
					])

					setAccessories([
						...(data.accessories || []).map((item: any) => ({
							id: item.partnumber,
							title: item.model,
							// ⚠️In new PIM we do not retrieve category anymore but products (since category can be multiple). But get products from db for many items can be expensive.
							bodyExcerpt:
								item.shortDescriptionsIntl?.[lang] ||
								item.descriptionsIntl?.[lang] ||
								item.model,
							imageSrc: item.thumbnail
								? item.thumbnail +
									'/jcr:content/renditions/cq5dam.web.400.400.jpeg'
								: '/images/box.svg',
							url: `/products/${item.partnumber}`,
						})),
					])

					// Update tracking refs only on successful request
					lastProcessedQueryRef.current = query
					lastProcessedLangRef.current = lang
				})
				.catch((error) => {
					// Only log errors that aren't from aborting
					if (error.name !== 'AbortError') {
						console.error('Search request failed:', error)
					}
				})
				.finally(() => {
					setLoading(false)
				})

			return () => {
				abortController?.abort()
			}
		}, 100) // Small delay to prevent rapid successive requests

		return () => {
			if (requestTimeoutRef.current) {
				clearTimeout(requestTimeoutRef.current)
			}
		}
	}, [query, lang])

	return {
		loading,
		products,
		accessories,
		resources,
		dt,
	}
}
